<template>
  <div class="q-pa-md">
    <div :class="$q.dark.mode ? 'text-grey-2' : 'text-primary'">
      <p class="text-body1 text-bold">
        Broker Analytics Intraday of {{ symbol }}
      </p>
    </div>

    <div class="row q-gutter-sm q-pb-sm q-pl-xs">
      <q-input
        dense
        filled
        v-model="fromDateAndTime"
        label="From Date and Time"
        hint="Select starting date and time"
        hint-color="grey-6"
      >
        <template v-slot:prepend>
          <q-icon name="event" class="cursor-pointer">
            <q-popup-proxy
              cover
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="fromDateAndTime"
                mask="YYYY-MM-DD HH:mm"
                @update:model-value="loadData()"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="Close" color="primary" flat />
                </div>
              </q-date>
            </q-popup-proxy>
          </q-icon>
        </template>

        <template v-slot:append>
          <q-icon name="access_time" class="cursor-pointer">
            <q-popup-proxy
              cover
              transition-show="scale"
              transition-hide="scale"
            >
              <q-time
                v-model="fromDateAndTime"
                mask="YYYY-MM-DD HH:mm"
                format12h
                @update:model-value="loadData()"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="Close" color="primary" flat />
                </div>
              </q-time>
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>

      <q-input
        dense
        filled
        v-model="toDateAndTime"
        label="To Date and Time"
        hint="Select ending date and time"
        hint-color="grey-6"
      >
        <template v-slot:prepend>
          <q-icon name="event" class="cursor-pointer">
            <q-popup-proxy
              cover
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="toDateAndTime"
                mask="YYYY-MM-DD HH:mm"
                @update:model-value="loadData()"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="Close" color="primary" flat />
                </div>
              </q-date>
            </q-popup-proxy>
          </q-icon>
        </template>

        <template v-slot:append>
          <q-icon name="access_time" class="cursor-pointer">
            <q-popup-proxy
              cover
              transition-show="scale"
              transition-hide="scale"
            >
              <q-time
                v-model="toDateAndTime"
                mask="YYYY-MM-DD HH:mm"
                format12h
                @update:model-value="loadData()"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="Close" color="primary" flat />
                </div>
              </q-time>
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
    </div>
    <q-card flat bordered>
      <q-tabs
        v-model="tabs"
        narrow-indicator
        class="q-pb-xs"
        dense
        align="left"
        :indicator-color="
          tabs === 'top-buy'
            ? 'green-3'
            : tabs === 'top-sell'
            ? 'red-3'
            : 'grey-7'
        "
        :active-color="
          tabs === 'top-buy'
            ? 'green-8'
            : tabs === 'top-sell'
            ? 'red-8'
            : 'grey-7'
        "
        :class="$q.dark.mode ? '' : 'text-grey-9'"
      >
        <q-tab name="top-buy" label="Top Buy" icon="shopping_cart" no-caps />
        <q-tab name="top-sell" label="Top Sell" icon="sell" no-caps />
      </q-tabs>

      <q-tabs
        v-model="tabsBroker"
        dense
        outside-arrows
        mobile-arrows
        :class="$q.dark.mode === true ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
        active-color="white"
        :active-bg-color="$q.dark.mode === true ? 'grey-9' : 'blue-4'"
        :indicator-color="$q.dark.mode === true ? 'grey-7' : 'blue-5'"
        inline-label
        align="justify"
        narrow-indicator
      >
        <q-tab
          name="broker_analytics_intraday"
          label="Broker Analytics Intraday"
          no-caps
        />
        <q-tab
          name="broker_analytics_intraday_details"
          label="Broker Analytics Intraday Details"
          no-caps
        />
      </q-tabs>

      <q-tab-panels v-model="tabsBroker" animated>
        <q-tab-panel class="q-px-none q-py-xs" :name="tabsBroker">
          <q-table
            :class="
              $q.dark.mode
                ? 'table-sticky-header-column-dark'
                : 'table-sticky-header-column'
            "
            :rows="computedTableData"
            :columns="computedColumns"
            :pagination="pagination"
            flat
            bordered
            separator="cell"
          >
            <template v-slot:body-cell-symbol="props">
              <q-td :props="props">
                <div class="col">
                  {{ props.value }}
                  <q-tooltip>{{
                    allStocks.find((stock) => stock.symbol == props.value)?.name
                  }}</q-tooltip>
                </div>
              </q-td>
            </template>
            <template v-slot:body-cell-buyer="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: { brokerID: props.value, transaction: 'Purchase' },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>

            <template v-slot:body-cell-seller="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: { brokerID: props.value, transaction: 'Selling' },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>

            <template v-slot:bottom-row>
              <q-tr :class="$q.dark.mode ? 'bg-grey-9' : 'bg-grey-4'">
                <q-td class="text-center" colspan="1">Total</q-td>
                <q-td></q-td>
                <q-td></q-td>
                <q-td class="text-center">
                  {{ sumBy(computedTableData, "quantity").toFixed(2) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(computedTableData, "amount") /
                      sumBy(computedTableData, "quantity")
                    ).toFixed(2)
                  }}
                </q-td>
                <q-td class="text-center">
                  {{ sumBy(computedTableData, "amount").toFixed(2) }}
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useQuasar } from "quasar";
import useData from "src/use/data";
import { useStore } from "vuex";
import { sumBy, meanBy } from "lodash";
import moment from "moment";

const $q = useQuasar();

const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
});

const { TopBuyIntraday, TopSellIntraday, brokerList } = useData();
const store = useStore();

const tabelData = ref([]);
const allStocks = ref([]);
const brokerLists = ref([]);

const tabs = ref("top-buy");
const tabsBroker = ref("broker_analytics_intraday");
const is_sum = ref(true);

const fromDateAndTime = ref("2024-09-15 11:00");
const toDateAndTime = ref("2024-09-15 12:00");

const loadData = async () => {
  $q.loading.show({});
  try {
    tabelData.value = [];

    const fetchFunction =
      tabs.value === "top-buy" ? TopBuyIntraday : TopSellIntraday;

    const fromDateUnix = convertToUnixTimestamp(fromDateAndTime.value);
    const toDateUnix = convertToUnixTimestamp(toDateAndTime.value);

    const response = await fetchFunction({
      from_timestamp: fromDateUnix,
      to_timestamp: toDateUnix,
      symbol: props.symbol,
      is_sum: is_sum.value,
    });

    tabelData.value = response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    $q.loading.hide();
  }
};

const computedTableData = computed(() => {
  return tabelData.value;
});

const pagination = ref({
  sortBy: "date",
  descending: true,
  rowsPerPage: 10,
});

const columns = [
  {
    name: "symbol",
    required: true,
    label: "Symbol",
    field: "symbol",
    sortable: true,
    align: "center",
  },

  {
    name: "date",
    required: true,
    label: "Date",
    field: "date",
    sortable: true,
    format: (val, row) => {
      if (val) {
        return convertDate(val);
      }

      const formatTime = (datetime) => {
        const date = new Date(datetime);
        return `${date.getHours()} : ${date
          .getMinutes()
          .toString()
          .padStart(2, "0")}`;
      };

      return `${formatTime(fromDateAndTime.value)} - ${formatTime(
        toDateAndTime.value
      )}`;
    },
    align: "center",
  },
  {
    name: "quantity",
    required: true,
    label: "Quantity",
    field: "quantity",
    sortable: true,
    align: "center",
  },
  {
    name: "rate",
    required: true,
    label: "Rate",
    field: "rate",
    sortable: true,
    align: "center",
  },
  {
    name: "amount",
    required: true,
    label: "Amount",
    field: "amount",
    sortable: true,
    align: "center",
  },
];

const buyColumns = [
  ...columns.slice(0, 2),

  {
    name: "buyer",
    required: true,
    label: "Buyer",
    field: "buyer",
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  ...columns.slice(2),
];

const sellColumns = [
  ...columns.slice(0, 2),
  {
    name: "seller",
    required: true,
    label: "Seller",
    field: "seller",
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  ...columns.slice(2),
];

const computedColumns = computed(() => {
  return tabs.value === "top-buy"
    ? buyColumns
    : tabs.value === "top-sell"
    ? sellColumns
    : [];
});

watch(tabs, (newTabs, oldTabs) => {
  loadData();
});

watch(tabsBroker, (newVal, oldVal) => {
  if (newVal === "broker_analytics_intraday") {
    is_sum.value = true;
  } else {
    is_sum.value = false;
  }
  loadData();
});

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

function convertToUnixTimestamp(dateString) {
  const date = new Date(dateString);
  return Math.floor(date.getTime() / 1000);
}

const setDatesAndLoadData = (dateString) => {
  const fromDate = new Date(dateString);
  fromDate.setDate(fromDate.getDate());
  fromDate.setHours(11, 0, 0, 0);

  const toDate = new Date(dateString);
  toDate.setDate(toDate.getDate());
  toDate.setHours(14, 59, 0, 0);

  fromDateAndTime.value = formatDate(fromDate);
  toDateAndTime.value = formatDate(toDate);

  loadData();
};

onMounted(async () => {
  brokerLists.value = (await brokerList()).data;
  if (store.state.market.asOf) {
    const getDate = store.state.market.asOf;
    setDatesAndLoadData(getDate);
  } else {
    store.dispatch("market/marketStatusNew").then(() => {
      const getDate = store.state.market.asOf;
      setDatesAndLoadData(getDate);
    });
  }

  allStocks.value = store.state.core.allStocks || [];
  if (!allStocks.value.length) {
    await store.dispatch("core/getAllStocks");
    allStocks.value = store.state.core.allStocks;
  }
});

const convertDate = (date) => moment(date).format("YYYY-MM-DD, hh:mm A");
</script>
